type IntersectionCallback = (entry: IntersectionObserverEntry) => void;

interface SharedObserver {
  observer: IntersectionObserver;
  callbacks: Map<Element, IntersectionCallback>;
}

type ObserverGroup = Map<string, SharedObserver>;

const viewportGroup: ObserverGroup = new Map();
const rootGroups = new WeakMap<Element | Document, ObserverGroup>();

const getGroup = (root: Element | Document | null | undefined): ObserverGroup => {
  if (!root) {
    return viewportGroup;
  }

  const existing = rootGroups.get(root);

  if (existing) {
    return existing;
  }

  const created: ObserverGroup = new Map();

  rootGroups.set(root, created);

  return created;
};

const createSharedObserver = (init: IntersectionObserverInit): SharedObserver => {
  const callbacks = new Map<Element, IntersectionCallback>();
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const callback = callbacks.get(entry.target);

      if (callback) {
        callback(entry);
      }
    });
  }, init);

  return { observer, callbacks };
};

interface ObserveIntersectionProps {
  target: Element;
  callback: IntersectionCallback;
  root?: Element | Document | null;
  threshold?: number;
  rootMargin?: string;
}

export const observeIntersection = ({
  target,
  callback,
  root,
  threshold = 0,
  rootMargin = '0px',
}: ObserveIntersectionProps) => {
  const group = getGroup(root);
  const key = `${threshold}|${rootMargin}`;
  const shared = group.get(key) ?? createSharedObserver({ root: root ?? undefined, threshold, rootMargin });

  group.set(key, shared);
  shared.callbacks.set(target, callback);
  shared.observer.observe(target);

  return () => {
    shared.observer.unobserve(target);
    shared.callbacks.delete(target);

    if (shared.callbacks.size === 0) {
      shared.observer.disconnect();
      group.delete(key);
    }
  };
};
